<template>
  <div ref="componentRef" class="top-bar" :class="{ 'is--open': menuIsOpen }">
    <nav class="nav-bar" aria-label="Hoofdmenu" role="navigation">
      <button
        class="menu-bars"
        :aria-label="menuIsOpen ? 'Menu sluiten' : 'Menu openen'"
        :disabled="isErrorLayout"
        @click="toggleMenu"
      >
        <Icon :name="menuIcon" aria-hidden="true" />
      </button>

      <div class="logo">
        <component
          :is="route.path === '/' ? 'div' : NuxtLink"
          :external="isErrorLayout"
          :to="route.path !== '/' ? `/` : undefined"
          :aria-label="route.path !== '/' ? 'home' : undefined"
          class="logo-link"
          @click="handleLinkClick('Terug naar Home', '/', 'topbar')"
          @click.middle="handleLinkClick('Terug naar Home', '/', 'topbar')"
        >
          <Icon name="eenvandaag-general:1v-logo" aria-hidden="true" role="presentation" />
        </component>
      </div>

      <ul v-if="computedLinks?.length > 0" ref="menuRef" class="nav-bar-menu" role="menubar">
        <template v-for="item in menu" :key="item.label">
          <li class="nav-bar-menu-item">
            <NuxtLink
              :to="item.url"
              class="nav-bar-menu-item-link"
              :external="hasExternalLinks || isErrorLayout"
              :class="{ active: item.active }"
              @click="
                (e) => {
                  onClick(e);
                  handleLinkClick(item.label, item.url, 'topbar-link');
                }
              "
              @click.middle="handleLinkClick(item.label, item.url, 'topbar-link')"
            >
              <span class="nav-bar-menu-item-link-label">{{ item.label }}</span>
            </NuxtLink>
          </li>
        </template>
      </ul>

      <div class="search" role="search">
        <NuxtLink
          class="search-link"
          to="/zoeken"
          :external="isErrorLayout"
          aria-label="Zoeken"
          @click="handleLinkClick('Zoeken', '/zoeken', 'topbar')"
          @click.middle="handleLinkClick('Zoeken', '/zoeken', 'topbar')"
        >
          <Icon name="eenvandaag-general:search" aria-hidden="true" role="presentation" />
        </NuxtLink>
      </div>

      <div
        class="nav-bar-menu-mobile-wrapper"
        :style="{ translate: menuIsOpen ? '0 0' : '-100% 0' }"
        :class="{ 'is--open': menuIsOpen }"
      >
        <template v-if="menuIsOpen">
          <ul v-if="computedLinks?.length > 0" class="nav-bar-menu-mobile">
            <template v-for="item in menu" :key="item.label">
              <li class="nav-bar-menu-mobile-item">
                <NuxtLink
                  :tabindex="menuIsOpen ? 0 : -1"
                  :to="item.url"
                  class="nav-bar-menu-mobile-item-link"
                  :class="{ active: item.active }"
                  :external="hasExternalLinks || isErrorLayout"
                  @click="handleLinkClick(item.label, item.url, 'topbar-mobile-link')"
                  @click.middle="handleLinkClick(item.label, item.url, 'topbar-mobile-link')"
                >
                  <span>{{ item.label }}</span>
                  <Icon
                    name="eenvandaag-general:chevron-right"
                    filled
                    class="nav-bar-menu-mobile-item-item-link-icon"
                    aria-hidden="true"
                    role="presentation"
                  />
                </NuxtLink>
              </li>
            </template>
          </ul>
          <div class="nav-bar-menu-mobile-socials-and-brands">
            <Socialkit :analytics="{ linkAnalyticsTag: 'topbar' }" />
            <div v-if="brands?.length > 0" class="nav-bar-menu-mobile-brands">
              <template v-for="brand in brands" :key="brand.title">
                <NuxtLink
                  v-if="brand.url"
                  :to="brand.url"
                  target="_blank"
                  :aria-label="brand.title"
                  :tabindex="menuIsOpen ? 0 : -1"
                  @click="handleLogoClick(brand.title, brand.url)"
                  @click.middle="handleLogoClick(brand.title, brand.url)"
                >
                  <Logo
                    v-if="brand.title === 'AVROTROS'"
                    class="nav-bar-menu-mobile-brand-logo-at"
                  />
                  <Icon
                    v-else
                    :name="brand.icon"
                    class="nav-bar-menu-mobile-brand-logo"
                    aria-hidden="true"
                    role="presentation"
                  />
                </NuxtLink>
              </template>
            </div>
          </div>
        </template>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import useGlobalConfigStore from '~/stores/global';
import handleLinkClick from '~/composables/handleLinkClick';
import siteConfig from '~~/configs/site.config';
import { NuxtLink } from '#components';

const globalConfigStore = useGlobalConfigStore();
const { menu } = globalConfigStore;
const menuRef = ref<HTMLElement | undefined>(undefined);
const componentRef = ref<HTMLElement | undefined>(undefined);

const brands = siteConfig?.logos || [];

const menuIsOpen = ref(false);
const menuIcon = ref('eenvandaag-general:bars');
const { $listen } = useNuxtApp();
const route = useRoute();
const activeLink = ref<string | undefined>(route.path);

$listen('closeMenu', () => {
  closeMenu();
});

watch(
  () => route.fullPath,
  () => {
    closeMenu();
    activeLink.value = route.path;
  },
);

defineProps({
  isErrorLayout: {
    type: Boolean,
    default: false,
  },
  hasExternalLinks: {
    type: Boolean,
    default: false,
  },
});

const toggleMenu = () => {
  menuIsOpen.value = !menuIsOpen.value;
  menuIcon.value = !menuIsOpen.value ? `eenvandaag-general:bars` : `eenvandaag-general:x-close`;
  window.scrollTo(0, 0);
  bodyScrollLock(menuIsOpen.value);
};

const closeMenu = () => {
  menuIsOpen.value = false;
  menuIcon.value = !menuIsOpen.value ? `eenvandaag-general:bars` : `eenvandaag-general:x-close`;
  bodyScrollLock(menuIsOpen.value);
};

useFocusTrap(componentRef, menuIsOpen);

const onClick = (e: Event) => {
  if (activeLink.value !== e.currentTarget?.href) {
    activeLink.value = undefined;
  }
};

const handleLogoClick = (linkText: string | undefined, linkUrl: string | undefined) => {
  gtmTrackEvent({
    linkAnalyticsTag: 'topbar-mobile-logo',
    linkText,
    linkUrl,
  });
};

const computedLinks = computed(() => {
  const clonedMenu = [...menu];

  clonedMenu.forEach((item) => {
    let isActive = false;

    if (item.url !== '/') {
      isActive = !!(activeLink.value === item.url || activeLink.value?.startsWith(item.url));
    }
    if (item.url === '/') {
      isActive = !!(
        activeLink.value === item.url || activeLink.value?.startsWith(item.url + 'themas')
      );
    }

    item.active = isActive;
  });

  return [...clonedMenu];
});
</script>

<style scoped lang="postcss">
.top-bar {
  width: 100%;
  background: var(--effect-gradient-primary);
  color: hsl(var(--color-neutral-white));

  &.is--open {
    background: hsla(var(--color-primary-950), 0.96) none;
  }
}

.nav-bar {
  --nav-bar-height: 56px;

  width: 100%;
  max-width: 1440px;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto;
  padding: 0 var(--unit-4);
  display: flex;
  gap: var(--unit-40);
  height: var(--nav-bar-height);

  @media (--lg-viewport) {
    --nav-bar-height: auto;

    min-height: 88px;
    padding: var(--unit-16) var(--unit-48);
  }
}

.menu-bars {
  border: unset;
  background: unset;
  padding: unset;
  margin: unset;
  display: flex;
  color: var(--color-neutral-white);
  width: var(--unit-44);
  height: var(--unit-48);
  justify-content: center;
  align-items: center;
  font-size: 24px;

  &:disabled {
    visibility: hidden;
  }

  @media (--lg-viewport) {
    display: none;
  }
}

.logo {
  line-height: 0;
  font-size: 32px;
  margin: 0;
  padding: 0;

  @media (--lg-viewport) {
    font-size: 48px;
  }
}

.logo-link {
  display: block;

  @add-mixin focus dark;
}

.nav-bar-menu {
  display: none;
  flex-shrink: 0;

  @media (--lg-viewport) {
    display: flex;
    gap: var(--unit-12);
  }
}

.nav-bar-menu-item {
  display: flex;
  cursor: pointer;

  @add-mixin title-md;
}

.nav-bar-menu-item-link {
  display: flex;
  height: 35px;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0 12px;
  box-shadow: none;
  transition: box-shadow 250ms ease-in-out;

  @add-mixin hover {
    color: hsl(var(--color-primary-300));
  }

  &:active {
    color: hsl(var(--color-primary-400));
  }

  @add-mixin focus dark {
    color: hsl(var(--color-primary-200));
  }

  &.active {
    .nav-bar-menu-item-link-label::after {
      scale: 1;
    }
  }
}

.nav-bar-menu-item-link-label {
  display: block;
  position: relative;
  transition: color 200ms ease-in-out;

  &::after {
    content: '';
    position: absolute;
    top: calc(100% + 3px);
    width: 100%;
    height: 2px;
    left: 0;
    background-color: hsl(var(--color-neutral-white));
    scale: 0;
    transition: scale 250ms ease-in-out;
  }
}

.nav-bar-menu-mobile-wrapper {
  --nav-bar-menu-mobile-wrapper-gap: 44px;

  position: fixed;
  top: var(--nav-bar-height);
  display: flex;
  flex-direction: column;
  background-color: hsla(var(--color-primary-950), 0.96);
  backdrop-filter: blur(8px);
  left: 0;
  width: 100%;
  z-index: 1001;
  height: calc(100svh - var(--nav-bar-height));

  justify-content: space-between;
  transition: translate 250ms ease-in-out;
  padding: var(--unit-48) var(--unit-40);
  overflow-y: auto;
  gap: var(--nav-bar-menu-mobile-wrapper-gap);

  @media (--lg-viewport) {
    display: none;
  }
}

.nav-bar-menu-mobile {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--unit-8);
  flex: 1 0 0;
  align-self: stretch;

  &-item {
    display: flex;
    padding: var(--unit-12) 0 var(--unit-20) 0;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-bottom: 1px solid hsla(var(--color-neutral-white), 0.15);

    @add-mixin heading-lg;
  }

  .router-link-active {
    color: hsl(var(--color-accent-bright-blue));
  }
}

.nav-bar-menu-mobile-item-link {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.nav-bar-menu-mobile-socials-and-brands {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit-32);
}

.nav-bar-menu-mobile-brands {
  display: flex;
  gap: var(--unit-16);
}

.nav-bar-menu-mobile-brand-logo {
  font-size: var(--unit-42);
}

.nav-bar-menu-mobile-brand-logo-at {
  height: var(--unit-42);
}

.search {
  flex-shrink: 1;
  display: flex;
  font-size: 24px;

  @media (--lg-viewport) {
    width: 100%;
    max-width: 221px;
    justify-content: flex-end;
  }
}

.search-link {
  width: var(--unit-44);
  height: var(--unit-48);
  display: flex;
  justify-content: center;
  align-items: center;

  @add-mixin focus dark;
}
</style>
